/**
 * GraphQL Error Handling Composable
 *
 * Provides standardized error handling for GraphQL operations with:
 * - Error code mapping to user-friendly messages
 * - Integration with application notification system
 * - Error state management for components
 *
 * The codes themselves are `TRANSPORT_ERROR_CODES` in `@/core/api/errorCodes`,
 * which is the one place they are listed; this module only adds the
 * user-facing sentence for each. To *branch* on a failure use the predicates
 * there — `parseGraphQLError` exists to produce display copy.
 */

import type { ApolloError } from "@apollo/client/core";
import type { GraphQLFormattedError } from "graphql";

import { readErrorCode, type TransportErrorCode } from "@/core/api/errorCodes";

// =============================================================================
// TYPES
// =============================================================================

/**
 * Every transport code the backend can emit, plus the two the client raises for
 * itself and an `UNKNOWN` floor.
 *
 * Derived from `TransportErrorCode` rather than listed by hand: this union used
 * to omit `UNAUTHORIZED`, `CONFLICT`, `MAINTENANCE` and the rest, so those all
 * fell through to `UNKNOWN` and were shown as "An unexpected error occurred".
 * `UNAUTHORIZED` is what `GraphQLExceptionHandler` emits for genuine
 * authentication failures, and it only ever reached "Please log in to continue"
 * because the code was being guessed from the message prose (#3142).
 * Deriving it means a code added to the vocabulary fails the exhaustive
 * `Record` below until someone writes its user-facing message.
 */
type GraphQLErrorCode = TransportErrorCode | "GRAPHQL_VALIDATION_FAILED" | "NETWORK_ERROR" | "UNKNOWN";

interface ParsedGraphQLError {
  code: GraphQLErrorCode;
  message: string;
  userMessage: string;
  originalError: ApolloError | Error | null;
  isRetryable: boolean;
}

// =============================================================================
// ERROR CODE MAPPINGS
// =============================================================================

const ERROR_MESSAGES: Record<GraphQLErrorCode, string> = {
  UNAUTHENTICATED: "Please log in to continue",
  UNAUTHORIZED: "Please log in to continue",
  CLUB_ASSOCIATION_REQUIRED: "You must be a member or guest of this club",
  FORBIDDEN: "You don't have permission to perform this action",
  NOT_FOUND: "The requested item could not be found",
  CONFLICT: "Someone else changed this while you were editing. Refresh and try again",
  PAYMENT_REQUIRED: "This feature is not included in the club's current plan",
  BAD_USER_INPUT: "Please check your input and try again",
  BAD_REQUEST: "Please check your input and try again",
  MAINTENANCE: "ToastHost is undergoing maintenance. Please try again shortly",
  VIEW_AS_READ_ONLY: "You are viewing as another user, so this action is read-only",
  GRAPHQL_PARSE_FAILED:
    "This action is temporarily unavailable because the app is out of sync with the server. Refresh the page and try again. If it still fails, contact support.",
  GRAPHQL_VALIDATION_FAILED:
    "This action is temporarily unavailable because the app is out of sync with the server. Refresh the page and try again. If it still fails, contact support.",
  INTERNAL_SERVER_ERROR: "Something went wrong. Please try again later",
  NETWORK_ERROR: "Unable to connect. Please check your internet connection",
  UNKNOWN: "An unexpected error occurred",
};

const RETRYABLE_CODES = new Set<GraphQLErrorCode>(["INTERNAL_SERVER_ERROR", "NETWORK_ERROR"]);

// =============================================================================
// ERROR PARSING
// =============================================================================

/**
 * A validation failure is the one code the *client* raises rather than the
 * server: Apollo rejects an operation selecting a field the schema does not
 * have before it is ever sent, so there is no `extensions.code` to read and no
 * alternative to recognising the message. Every other code comes off
 * `extensions` (#3142) — this function used to fall back to matching prose for
 * all of them, and since the shared client resolves resolver errors onto
 * `result.errors` rather than rejecting, that fallback was what actually ran.
 */
function isSchemaValidationMessage(message: string): boolean {
  return message.toLowerCase().includes("cannot query field");
}

/**
 * Extract the error code from a GraphQL error's extensions, narrowed to the
 * codes this composable has a user-facing message for.
 */
function extractErrorCode(error: GraphQLFormattedError): GraphQLErrorCode {
  // Wrapped as a one-entry `errors` list, which is the shape `readErrorCode`
  // walks; a bare `GraphQLFormattedError` carries its code on `extensions`.
  const code = readErrorCode({ errors: [error as { extensions?: { code?: unknown } }] });
  if (code && code.toUpperCase() in ERROR_MESSAGES) {
    return code.toUpperCase() as GraphQLErrorCode;
  }

  return isSchemaValidationMessage(error.message) ? "GRAPHQL_VALIDATION_FAILED" : "UNKNOWN";
}

/**
 * Parse an Apollo error into a standardized format
 */
export function parseGraphQLError(error: ApolloError | Error | unknown): ParsedGraphQLError {
  // Handle null/undefined
  if (!error) {
    return {
      code: "UNKNOWN",
      message: "An unknown error occurred",
      userMessage: ERROR_MESSAGES.UNKNOWN,
      originalError: null,
      isRetryable: false,
    };
  }

  // Handle network errors. An HTTP-level rejection is not automatically a
  // connectivity failure: the server answers 401/403/503 with a
  // `GraphQLTransportError` envelope carrying an explicit `code`, and reporting
  // that as "Unable to connect. Please check your internet connection" tells the
  // user to check their wifi when their session has expired. So the explicit
  // code wins, and `NETWORK_ERROR` is the fallback for a rejection that carried
  // none — a dropped connection, DNS, a timeout (#3142).
  if (error instanceof Error && "networkError" in error && (error as ApolloError).networkError) {
    const transportCode = readErrorCode(error)?.toUpperCase();
    const code: GraphQLErrorCode =
      transportCode && transportCode in ERROR_MESSAGES ? (transportCode as GraphQLErrorCode) : "NETWORK_ERROR";
    return {
      code,
      message: (error as ApolloError).networkError?.message || "Network error",
      userMessage: ERROR_MESSAGES[code],
      originalError: error as ApolloError,
      // A genuine connectivity failure is worth retrying; a 403 is not.
      isRetryable: code === "NETWORK_ERROR" || RETRYABLE_CODES.has(code),
    };
  }

  // Handle GraphQL errors
  if (error instanceof Error && "graphQLErrors" in error) {
    const apolloError = error as ApolloError;
    const graphQLErrors = apolloError.graphQLErrors;

    if (graphQLErrors && graphQLErrors.length > 0) {
      const firstError = graphQLErrors[0];
      const code = extractErrorCode(firstError);

      return {
        code,
        message: firstError.message,
        userMessage: ERROR_MESSAGES[code],
        originalError: apolloError,
        isRetryable: RETRYABLE_CODES.has(code),
      };
    }
  }

  // Handle generic errors. An `ApiError` normalized by `runGraphQL` or
  // `toApiError` carries the server's code on `code`, which `readErrorCode`
  // returns; anything else genuinely has no code and is UNKNOWN rather than
  // guessed at from its wording (#3142).
  if (error instanceof Error) {
    const rawCode = readErrorCode(error)?.toUpperCase();
    const code: GraphQLErrorCode =
      rawCode && rawCode in ERROR_MESSAGES
        ? (rawCode as GraphQLErrorCode)
        : isSchemaValidationMessage(error.message)
          ? "GRAPHQL_VALIDATION_FAILED"
          : "UNKNOWN";
    return {
      code,
      message: error.message,
      userMessage: ERROR_MESSAGES[code],
      originalError: error,
      isRetryable: RETRYABLE_CODES.has(code),
    };
  }

  // Handle unknown error types
  return {
    code: "UNKNOWN",
    message: String(error),
    userMessage: ERROR_MESSAGES.UNKNOWN,
    originalError: null,
    isRetryable: false,
  };
}

// =============================================================================
// STRUCTURED LOGGING DETAIL
// =============================================================================

type ApolloErrorClassification = "abort" | "graphql" | "network" | "client" | "unknown";

interface ApolloErrorLogDetail {
  classification: ApolloErrorClassification;
  message: string;
  operationName?: string;
  graphQLErrors?: {
    message: string;
    path?: readonly (string | number)[];
    code?: string;
  }[];
  networkError?: {
    name?: string;
    message?: string;
    statusCode?: number;
    result?: unknown;
  };
}

/**
 * Extract structured diagnostic detail from an Apollo / network error so that
 * loggers can record actionable fields instead of the bare `ApolloError` class
 * name. Use alongside a `logger.warn`/`logger.error` call:
 *
 * ```ts
 * } catch (err) {
 *   logger.error("Failed to load X:", formatApolloErrorForLog(err));
 * }
 * ```
 */
export function formatApolloErrorForLog(error: unknown): ApolloErrorLogDetail {
  if (!error) {
    return { classification: "unknown", message: "no error provided" };
  }

  if (!(error instanceof Error)) {
    return { classification: "unknown", message: String(error) };
  }

  const apolloLike = error as ApolloError & {
    operation?: { operationName?: string };
  };

  const detail: ApolloErrorLogDetail = {
    classification: "unknown",
    message: error.message,
  };

  if (apolloLike.operation?.operationName) {
    detail.operationName = apolloLike.operation.operationName;
  }

  const networkError = apolloLike.networkError as
    | (Error & { statusCode?: number; result?: unknown })
    | null
    | undefined;
  if (networkError) {
    detail.networkError = {
      name: networkError.name,
      message: networkError.message,
      statusCode: networkError.statusCode,
      result: networkError.result,
    };
    // `TypeError: Failed to fetch` (browsers) and `TypeError: fetch failed`
    // (Node/undici) cover both aborts and genuine network failures; without
    // an AbortSignal here we cannot distinguish them, so err on the side of
    // labelling as "network" to avoid hiding real outages. Callers that hold
    // the signal should use isAbortOrTeardownError() from @/utils/requestAbort
    // for the finer distinction.
    detail.classification = networkError.name === "AbortError" ? "abort" : "network";
  }

  const graphQLErrors = apolloLike.graphQLErrors;
  if (graphQLErrors && graphQLErrors.length > 0) {
    detail.graphQLErrors = graphQLErrors.map((gqlErr) => {
      const extensions = gqlErr.extensions as Record<string, unknown> | undefined;
      const code = extensions?.code && typeof extensions.code === "string" ? extensions.code : undefined;
      return {
        message: gqlErr.message,
        path: gqlErr.path,
        code,
      };
    });
    if (detail.classification === "unknown") detail.classification = "graphql";
  }

  if (detail.classification === "unknown" && apolloLike.clientErrors && apolloLike.clientErrors.length > 0) {
    detail.classification = "client";
  }

  return detail;
}

// =============================================================================
// COMPOSABLE
// =============================================================================
